//import "./wdyr.js"

import { Auth0Provider } from "@auth0/auth0-react"
import "@fontsource/roboto/300.css"
import "@fontsource/roboto/400.css"
import "@fontsource/roboto/500.css"
import "@fontsource/roboto/700.css"
import { CssBaseline, ThemeProvider } from "@mui/material"
import { LicenseInfo } from "@mui/x-license"
import * as Sentry from "@sentry/react"
import { useEffect } from "react"
import ReactDOM from "react-dom/client"
import { Provider } from "react-redux"
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router"
import { BrowserRouter } from "react-router-dom"
import App from "./App"
import { ModeProvider } from "./ModeProvider"
import { store } from "./app/store"
import { Auth0AuthProvider } from "./auth/Auth0AuthProvider"
import { AuthStrategyProvider } from "./auth/AuthStrategyContext"
import { DevAuthProvider } from "./auth/DevAuthProvider"
import { ParentAuthProvider } from "./auth/ParentAuthProvider"
import { AuthErrorBoundary } from "./components/AuthErrorBoundary"
import { DebugDialogHost } from "./components/DebugDialogHost"
import DisableNumInputScroll from "./components/DisableNumInputScroll"
import { NotificationProvider } from "./components/NotificationProvider"
import "./i18n/i18n"
import "./index.css"
import { excludeBrowserPrintFromTraceTargets } from "./features/zebraPrint/sentryTraceTargets"
import { MrtProvider } from "./iot/MrtContext"
import { darkTheme, lightTheme } from "./theme"
import { consoleDebug, getAuthStrategy } from "./utils"

LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_X_PRO_LICENSE_KEY)

const rawDisableSentry = import.meta.env.VITE_DISABLE_SENTRY
const isSentryDisabled =
  typeof rawDisableSentry === "string" &&
  rawDisableSentry.toLowerCase() === "true"

if (!isSentryDisabled) {
  const traceTargetsRaw = import.meta.env.VITE_SENTRY_TRACE_PROPAGATION_TARGETS
  // LOCKER-522: never on the local Zebra Browser Print agent (CORS preflight).
  const tracePropagationTargets = excludeBrowserPrintFromTraceTargets(
    traceTargetsRaw
      ? traceTargetsRaw
          .split(",")
          .map((target: string) => target.trim())
          .filter(Boolean)
      : ["localhost"],
  )

  const releaseCandidate =
    (import.meta.env as Record<string, string | undefined>).VITE_APP_VERSION ||
    (import.meta.env as Record<string, string | undefined>).VITE_APP_BUILD_ID

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN || "missing_dsn",
    sendDefaultPii: true,
    environment:
      import.meta.env.VITE_SENTRY_ENVIRONMENT ||
      import.meta.env.MODE ||
      "development",
    release: releaseCandidate,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.consoleLoggingIntegration({
        levels: ["log", "warn", "error"],
      }),
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate:
      parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? "1.0") ||
      1.0,
    tracePropagationTargets,
    enableMetrics: import.meta.env.VITE_SENTRY_ENABLE_METRICS === "true",
    replaysSessionSampleRate:
      parseFloat(
        import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE ?? "0.1",
      ) || 0.1,
    replaysOnErrorSampleRate:
      parseFloat(
        import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE ?? "1.0",
      ) || 1.0,
  })
}

// Agentic mode short-circuits the real auth strategy entirely. This is only
// ever true in Playwright/dev — production builds never set VITE_AGENTIC.
const isAgentic = import.meta.env.VITE_AGENTIC === "true"

/**
 * `?theme=dark` under VITE_AGENTIC only — a screenshot seam, not a feature.
 *
 * The SA console has no theme switcher and this does not add one: the toggle is
 * unreachable in any build that does not set `VITE_AGENTIC`, which production
 * never does (same gate as `DevAuthProvider` and `__mrtTestBus` above). It exists
 * so a Playwright spec can capture the same screen in both palettes and prove a
 * component reads its colours from the theme rather than hardcoding them — the
 * defect `Sstv2FleetRollup`'s `bgcolor: "white"` is an example of, and which no
 * light-only screenshot can catch.
 *
 * If the console ever grows a real dark mode, this becomes the persisted
 * preference and the query param stays as the override.
 */
const agenticThemeOverride = (): typeof lightTheme => {
  if (!isAgentic) return lightTheme
  const wanted = new URLSearchParams(window.location.search).get("theme")
  return wanted === "dark" ? darkTheme : lightTheme
}

// Determine auth strategy. In agentic mode we report "auth0" as a stable
// value (DevAuthProvider also publishes this via AuthStrategyProvider) but
// the real Auth0Provider is never mounted.
const authStrategy = isAgentic ? "auth0" : getAuthStrategy()

consoleDebug("Auth Strategy:", authStrategy)

window.addEventListener("beforeunload", (event) => {
  console.trace("Page is about to reload or navigate away")
})

declare global {
  interface Window {
    baseName: string
  }
}

const AppWrapper = () => {
  const viteBasePath = import.meta.env.VITE_BASE_PATH ?? "/"

  // @ts-ignore
  const version = import.meta.env.VITE_APP_VERSION

  // @ts-ignore
  const buildId = import.meta.env.VITE_APP_BUILD_ID

  console.log(`App version: ${version} Build ID: ${buildId}`)

  useEffect(() => {
    if (isSentryDisabled) {
      return
    }

    try {
      Sentry.setTag("auth_strategy", authStrategy)
      if (Sentry.metrics?.count) {
        Sentry.metrics.count("sl2.auth_strategy.resolved", 1, {
          attributes: {
            strategy: authStrategy,
          },
        })
      }
    } catch (error) {
      consoleDebug("Failed to push auth strategy metric", error)
    }
  }, [])

  const renderAuthProvider = () => {
    if (isAgentic) {
      return (
        <AuthErrorBoundary>
          <DevAuthProvider>
            <MrtProvider>
              <NotificationProvider>
                <App />
              </NotificationProvider>
              <DebugDialogHost version={version} buildId={buildId} />
            </MrtProvider>
          </DevAuthProvider>
        </AuthErrorBoundary>
      )
    }
    switch (authStrategy) {
      case "auth0":
        return (
          <AuthErrorBoundary>
            <Auth0Provider
              domain={import.meta.env.VITE_AUTH0_DOMAIN}
              clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
              authorizationParams={{
                audience: "mbls-global-api",
                redirect_uri: window.location.origin,
              }}
              cacheLocation="localstorage"
            >
              <Auth0AuthProvider>
                <MrtProvider>
                  <NotificationProvider>
                    <App />
                  </NotificationProvider>
                  <DebugDialogHost version={version} buildId={buildId} />
                </MrtProvider>
              </Auth0AuthProvider>
            </Auth0Provider>
          </AuthErrorBoundary>
        )
      case "parent":
        return (
          <AuthErrorBoundary>
            <ParentAuthProvider>
              <MrtProvider>
                <NotificationProvider>
                  <App />
                </NotificationProvider>
                <DebugDialogHost version={version} buildId={buildId} />
              </MrtProvider>
            </ParentAuthProvider>
          </AuthErrorBoundary>
        )
      default:
        return null
    }
  }

  return (
    <div>
      <DisableNumInputScroll />
      <AuthStrategyProvider strategy={authStrategy}>
        <ThemeProvider theme={agenticThemeOverride()}>
          {/*
            AGENTIC ONLY, and deliberately not mounted in production (round-2 F2.3).

            Nothing paints the page ground from `theme.palette.background.default`
            — there is no `CssBaseline` anywhere and `index.css`'s `body` rule sets
            no background — so under `?theme=dark` every `Paper` painted itself
            dark while the page behind them stayed light, and `darkTheme`'s light
            `text.secondary` landed on it. The dark capture was half unreadable for
            a reason unrelated to the components it was taken to prove.

            Scoped behind the same `VITE_AGENTIC` gate as the theme override
            itself: mounting `CssBaseline` app-wide would change how every
            production page renders, which is a real change this lane has no
            mandate for. If the console ever grows a real dark mode, that is the
            moment to mount it properly and to delete this.
          */}
          {isAgentic && <CssBaseline />}
          <ModeProvider>
            <BrowserRouter basename={window.baseName || viteBasePath}>
              {renderAuthProvider()}
            </BrowserRouter>
          </ModeProvider>
        </ThemeProvider>
      </AuthStrategyProvider>
    </div>
  )
}

const reactRoot = ReactDOM.createRoot(
  document.getElementById("root")!,
  isSentryDisabled
    ? undefined
    : {
        onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
          console.warn("Uncaught error", error, errorInfo.componentStack)
        }),
        onCaughtError: Sentry.reactErrorHandler(),
        onRecoverableError: Sentry.reactErrorHandler(),
      },
)

reactRoot.render(
  <Provider store={store}>
    <AppWrapper />
  </Provider>,
)
